import type { HTMLAttributes, ReactNode } from "react";
import { createStyles } from "./styling";

import { BrandMark } from "./BrandMark";
import { ColorModeButton } from "./ColorMode";
import { LiquidGlass } from "./LiquidGlass";
import { VisualEffectsButton } from "./VisualEffects";

const useStyles = createStyles(({ css, token }) => ({
  accessPanel: css`
    background: ${token.colorBgContainer};
    background: color-mix(in srgb, ${token.colorBgContainer} 97%, transparent);
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: 16px;
    box-shadow: 0 18px 60px rgb(31 35 41 / 10%);
    box-sizing: border-box;
    max-width: 520px;
    padding: 34px;
    text-align: center;
    width: 100%;

    @media (max-width: ${token.screenXSMax}px) {
      border-radius: 14px;
      padding: 28px 22px;
    }

    @media (prefers-contrast: more) {
      border-color: ${token.colorText};
      box-shadow: none;
    }
  `,
  accessPanelBody: css`
    color: ${token.colorTextSecondary};
    line-height: 1.7;
    margin: 0 auto 24px;
    max-width: 420px;
  `,
  accessPanelIcon: css`
    align-items: center;
    background: ${token.colorPrimaryBg};
    border: 1px solid ${token.colorPrimaryBorder};
    border-radius: 14px;
    color: ${token.colorPrimary};
    display: inline-flex;
    font-size: 28px;
    height: 56px;
    justify-content: center;
    margin-bottom: 20px;
    width: 56px;
  `,
  accessPanelTitle: css`
    color: ${token.colorText};
    font-size: 24px;
    font-weight: 680;
    letter-spacing: -0.025em;
    line-height: 1.3;
    margin: 0 0 10px;
  `,
  actions: css`
    align-items: center;
    display: flex;
    flex: none;
    gap: 8px;
    justify-content: flex-end;
    min-width: 0;
  `,
  actionContent: css`
    align-items: center;
    display: flex;
    gap: 6px;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    > * {
      flex: none;
    }

    @media (max-width: ${token.screenXSMax}px) {
      max-width: 58vw;

      button {
        min-height: 44px;
        min-width: 44px;
      }
    }
  `,
  brand: css`
    align-items: center;
    display: flex;
    gap: 14px;
    min-width: 0;
  `,
  context: css`
    color: ${token.colorTextSecondary};
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::before {
      background: ${token.colorBorder};
      content: "";
      display: inline-block;
      height: 14px;
      margin-right: 14px;
      vertical-align: -2px;
      width: 1px;
    }

    @media (max-width: ${token.screenXSMax}px) {
      display: none;
    }
  `,
  header: css`
    align-items: center;
    background: ${token.colorBgContainer};
    background: color-mix(in srgb, ${token.colorBgContainer} 94%, transparent);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid ${token.colorBorderSecondary};
    display: flex;
    flex: 0 0 58px;
    gap: 18px;
    height: 58px;
    justify-content: space-between;
    padding: 0 clamp(16px, 4vw, 48px);
    position: relative;
    z-index: 1;

    @media (prefers-contrast: more) {
      border-color: ${token.colorText};
    }
  `,
  main: css`
    box-sizing: border-box;
    flex: 1;
    min-height: 0;
    overflow: auto;
    position: relative;
  `,
  page: css`
    background: ${token.colorBgLayout};
    background:
      radial-gradient(
        circle at 12% 4%,
        ${token.colorPrimaryBg} 0,
        transparent 24%
      ),
      ${token.colorBgLayout};
    color: ${token.colorText};
    display: flex;
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: hidden;
  `,
}));

export function PublicPageShell({
  actions,
  children,
  className,
  context = "安全外部访问",
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  actions?: ReactNode;
  context?: ReactNode;
}) {
  const { cx, styles } = useStyles();
  return (
    <div className={cx(styles.page, className)} {...props}>
      <LiquidGlass as="header" className={cx(styles.header, "ark-glass-bar")}>
        <div className={styles.brand}>
          <BrandMark />
          {context ? <span className={styles.context}>{context}</span> : null}
        </div>
        <div className={styles.actions}>
          {actions ? (
            <div className={styles.actionContent}>{actions}</div>
          ) : null}
          <ColorModeButton />
          <VisualEffectsButton />
        </div>
      </LiquidGlass>
      <main className={styles.main}>{children}</main>
    </div>
  );
}

export function PublicAccessPanel({
  children,
  description,
  icon,
  status,
  title,
}: {
  children?: ReactNode;
  description: ReactNode;
  icon: ReactNode;
  status?: "alert" | "status";
  title: ReactNode;
}) {
  const { styles } = useStyles();
  return (
    <section
      {...(status
        ? {
            "aria-live": status === "alert" ? "assertive" : "polite",
            role: status,
          }
        : {})}
      className={styles.accessPanel}
    >
      <div aria-hidden="true" className={styles.accessPanelIcon}>
        {icon}
      </div>
      <h1 className={styles.accessPanelTitle}>{title}</h1>
      <div className={styles.accessPanelBody}>{description}</div>
      {children}
    </section>
  );
}
